import React, { useMemo } from 'react';
import { formatLapSeconds } from '../../analytics/lapTime';
import { linearScale, niceTicks, padDomain } from './chartUtils';
import './charts.css';

export interface PaceEntry {
  teamId: string;
  kartNumber: string;
  label: string;
  paceSeconds: number;
  category?: string;
}

interface PaceDistributionProps {
  entries: PaceEntry[];
  ourTeamIds?: Set<string>;
  height?: number;
}

const MARGIN = { top: 10, right: 10, bottom: 20, left: 10 };
const VIEW_WIDTH = 720;

const CATEGORY_COLOUR: Record<string, string> = {
  pro: 'var(--cat-pro)',
  gold: 'var(--cat-gold)',
  silver: 'var(--cat-silver)',
};

/**
 * Every kart's pace on one axis, so the shape of the field is visible at a
 * glance — where the quick group ends, how big the gap to the next bunch is, and
 * where we sit in it. Dots are nudged vertically when they would overlap, which
 * keeps a tight midfield readable instead of collapsing to one blob.
 */
export const PaceDistribution: React.FC<PaceDistributionProps> = ({
  entries,
  ourTeamIds,
  height = 96,
}) => {
  const model = useMemo(() => {
    const usable = entries.filter((e) => Number.isFinite(e.paceSeconds) && e.paceSeconds > 0);
    if (usable.length < 2) return null;

    const paces = usable.map((e) => e.paceSeconds);
    const domain = padDomain([Math.min(...paces), Math.max(...paces)], 0.04);
    const x = linearScale(domain, [MARGIN.left, VIEW_WIDTH - MARGIN.right]);

    const laneHeight = 9;
    const laneCount = Math.max(1, Math.floor((height - MARGIN.top - MARGIN.bottom) / laneHeight));
    const occupied: number[] = [];

    const dots = [...usable]
      .sort((a, b) => a.paceSeconds - b.paceSeconds)
      .map((entry) => {
        const cx = x(entry.paceSeconds);
        // Drop into the first lane whose last dot is clear of this one.
        let lane = 0;
        while (lane < laneCount && (occupied[lane] ?? -Infinity) > cx - 7) lane += 1;
        if (lane >= laneCount) lane = laneCount - 1;
        occupied[lane] = cx;

        return {
          ...entry,
          cx,
          cy: MARGIN.top + lane * laneHeight + laneHeight / 2,
          ours: ourTeamIds?.has(entry.teamId) ?? false,
        };
      });

    return {
      dots,
      ticks: niceTicks(domain[0], domain[1], 5).map((v) => ({ v, x: x(v) })),
    };
  }, [entries, ourTeamIds, height]);

  if (!model) {
    return <div className="chart-empty">Not enough pace data yet.</div>;
  }

  return (
    <svg
      className="chart"
      viewBox={`0 0 ${VIEW_WIDTH} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label="Distribution of clean average lap times across the field"
    >
      {model.ticks.map((t) => (
        <g key={t.v}>
          <line
            className="chart-grid-line"
            x1={t.x}
            x2={t.x}
            y1={MARGIN.top - 4}
            y2={height - MARGIN.bottom}
          />
          <text className="chart-axis-label" x={t.x} y={height - 6} textAnchor="middle">
            {formatLapSeconds(t.v)}
          </text>
        </g>
      ))}

      {model.dots.map((d) => (
        <circle
          key={d.teamId}
          className={`chart-dot${d.ours ? ' chart-dot--ours' : ''}`}
          cx={d.cx}
          cy={d.cy}
          r={d.ours ? 4 : 3}
          style={{
            fill: CATEGORY_COLOUR[(d.category ?? '').toLowerCase()] ?? 'var(--cat-fallback)',
          }}
        >
          <title>
            #{d.kartNumber} {d.label} — {formatLapSeconds(d.paceSeconds)}
          </title>
        </circle>
      ))}
    </svg>
  );
};

export default PaceDistribution;
